/**
 * Skyrim-style horizontal compass bar. Fixed at the top-center of the
 * screen (NOT a draggable window). Renders the player's facing direction
 * as the center of a degree strip; cardinal labels and guard markers scroll
 * left/right as the player rotates. Guards appear at their real bearing
 * relative to the player.
 */

import type { Guard } from '../sim/guard';
import type { Player } from '../sim/player';

/** How wide (in degrees) the visible slice of the compass is. */
const FIELD_DEG = 160;
const HALF_FIELD = FIELD_DEG / 2;

const CARDINALS: Array<{ label: string; deg: number }> = [
  { label: 'N', deg: 0 },
  { label: 'NE', deg: 45 },
  { label: 'E', deg: 90 },
  { label: 'SE', deg: 135 },
  { label: 'S', deg: 180 },
  { label: 'SW', deg: 225 },
  { label: 'W', deg: 270 },
  { label: 'NW', deg: 315 },
];

export class CompassBar {
  private readonly root: HTMLElement;
  private readonly strip: HTMLElement;
  private readonly ticks: HTMLElement;
  private readonly markersEl: HTMLElement;
  private readonly labelEl: HTMLElement;
  private readonly focusModeEl!: HTMLElement;
  private cardinalsCache: HTMLElement[] = [];

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.classList.add('compass-bar');
    this.root.innerHTML = `
      <div class="cb-strip">
        <div class="cb-ticks"></div>
        <div class="cb-cardinals"></div>
        <div class="cb-markers"></div>
        <div class="cb-center-mark">▼</div>
      </div>
      <div class="cb-label">N · 0°</div>
      <div class="cb-focus-mode">FOCUS MODE</div>
    `;
    this.strip = this.root.querySelector('.cb-strip') as HTMLElement;
    this.ticks = this.root.querySelector('.cb-ticks') as HTMLElement;
    this.markersEl = this.root.querySelector('.cb-markers') as HTMLElement;
    this.labelEl = this.root.querySelector('.cb-label') as HTMLElement;
    this.focusModeEl = this.root.querySelector('.cb-focus-mode') as HTMLElement;
    const cardinalsHost = this.root.querySelector('.cb-cardinals') as HTMLElement;
    // Build 3 copies of the cardinal ring (−360, 0, +360) so we can wrap.
    for (let repeat = -1; repeat <= 1; repeat++) {
      for (const c of CARDINALS) {
        const el = document.createElement('span');
        el.className = 'cb-cardinal';
        el.textContent = c.label;
        el.dataset.deg = String(c.deg + repeat * 360);
        cardinalsHost.appendChild(el);
        this.cardinalsCache.push(el);
      }
    }
    // Tick marks every 10°.
    for (let d = -180; d <= 540; d += 10) {
      const t = document.createElement('span');
      t.className = 'cb-tick' + (d % 30 === 0 ? ' cb-tick-major' : '');
      t.dataset.deg = String(d);
      this.ticks.appendChild(t);
    }
  }

  update(player: Player, guards: readonly Guard[]): void {
    const facingDeg = facingBearing(player.facingDx, player.facingDy);
    const width = this.strip.clientWidth || 700;

    // Position cardinals
    for (const el of this.cardinalsCache) {
      const deg = Number(el.dataset.deg ?? '0');
      const x = degToStripX(deg, facingDeg, width);
      if (x < -50 || x > width + 50) {
        el.style.display = 'none';
      } else {
        el.style.display = '';
        el.style.left = `${x}px`;
      }
    }
    // Position ticks
    for (const el of this.ticks.children as HTMLCollectionOf<HTMLElement>) {
      const deg = Number(el.dataset.deg ?? '0');
      const x = degToStripX(deg, facingDeg, width);
      if (x < -20 || x > width + 20) {
        el.style.display = 'none';
      } else {
        el.style.display = '';
        el.style.left = `${x}px`;
      }
    }

    // Guard markers — focused guard gets a neon-alert glow at its bearing.
    this.markersEl.innerHTML = '';
    for (const g of guards) {
      if (g.body.dead || !g.everSeenByPlayer) continue;
      const gx = g.marked ? g.x : g.lastSeenX;
      const gy = g.marked ? g.y : g.lastSeenY;
      const dx = gx - player.x;
      const dy = gy - player.y;
      if (dx === 0 && dy === 0) continue;
      const bearing = bearingOf(dx, dy);
      const x = degToStripX(bearing, facingDeg, width);
      if (x < -30 || x > width + 30) continue;
      const isFocused = player.focusedTargetId === g.id;
      const m = document.createElement('div');
      m.className = `cb-marker cb-marker-${g.state}${isFocused ? ' cb-marker-focused' : ''}`;
      m.style.left = `${x}px`;
      m.title = `Guard #${g.id} · ${g.state}${isFocused ? ' · FOCUS' : ''}`;
      m.textContent = 'g';
      this.markersEl.appendChild(m);
    }

    // Header label — most recent cardinal + degree.
    this.labelEl.textContent = `${nearestCardinal(facingDeg)} · ${facingDeg.toFixed(0)}°`;

    // Glowing yellow FOCUS MODE indicator while focus mode is active.
    this.focusModeEl.classList.toggle('on', player.focusActive);
  }
}

/** Convert a facing (dx, dy) with y-down world axis into a compass bearing
 *  0..360 where N=0, E=90, S=180, W=270. */
function facingBearing(dx: number, dy: number): number {
  if (dx === 0 && dy === 0) return 0;
  // atan2(dx, -dy) puts +y pointing up (N is (0, -1)).
  const rad = Math.atan2(dx, -dy);
  const deg = (rad * 180) / Math.PI;
  return (deg + 360) % 360;
}

function bearingOf(dx: number, dy: number): number {
  return facingBearing(dx, dy);
}

/** Position `deg` on the strip so `facingDeg` maps to strip center.
 *  Returns x in pixels (relative to strip left edge). */
function degToStripX(deg: number, facingDeg: number, stripWidth: number): number {
  let delta = deg - facingDeg;
  while (delta > 180) delta -= 360;
  while (delta < -180) delta += 360;
  // delta in ±180; we only show ±HALF_FIELD.
  const t = (delta + HALF_FIELD) / FIELD_DEG;
  return t * stripWidth;
}

function nearestCardinal(deg: number): string {
  let best = CARDINALS[0]!;
  let bestD = Infinity;
  for (const c of CARDINALS) {
    let d = Math.abs(c.deg - deg);
    if (d > 180) d = 360 - d;
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }
  return best.label;
}
